<template>
  <v-card height="100%" tile id="companies">
    <v-card-title>
      <v-toolbar flat>
        <v-toolbar-title class="col-6">{{ resx.labels.companies }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-text-field
          append-icon="search"
          :label="resx.labels.search"
          single-line
          hide-details
          v-model="search"
        >
        </v-text-field>
      </v-toolbar>
    </v-card-title>
    <v-card-text class="table-container fixed-header">
      <v-row row class="count-items">
        <v-spacer></v-spacer>
        <v-col shrink>
          <span v-if="companies.length > 0">{{
            `${
              resx.labels.countItems
            } ${resx.labels.companies.toLowerCase()}: ${count}`
          }}</span>
        </v-col>
      </v-row>
      <v-row column fill-height>
        <v-col fill-height class="scrollable">
          <v-data-table
            :class="{ 'layout-fixed': $vuetify.breakpoint.lgAndUp }"
            :headers="headers"
            :items="companies"
            :search="search"
            :loading=loading
            :no-data-text="resx.messages.noCompaniesAvailable"
            :no-results-text="resx.messages.noCompaniesFound"
            :items-per-page="itemsPerPage"
            :footer-props="{ itemsPerPageOptions: [15, 50, 100], itemsPerPageText: 'Mostrar' }"
            @update:items-per-page="saveItemsPerPage"
            ref="dataTable"
            sort-by="name"
            loading-text=""
          >
          <template v-slot:loading>
            <BarLoader :isLoading=loading />
          </template>
            <template v-slot:item.name="{ item }">
              <td class="word-wrap">
                <a @click="editCompany(item)">{{ item.name }}</a>
              </td>
            </template>
            <template v-slot:item.identifier="{ item }">
              <td class="hidden-sm-and-down word-wrap">
                {{
                  `${item.identifier.type.toUpperCase()}: ${
                    item.identifier.number
                  }`
                }}
              </td>
            </template>
            <template v-slot:item.isActive="{ item }">
              <td
                class="hidden-sm-and-down"
                :class="[item.isActive ? 'success--text' : 'error--text']"
              >
                {{ item.isActive ? resx.labels.active : resx.labels.inactive }}
              </td>
            </template>
            <template v-slot:item.phone="{ item }">
              <td class="hidden-sm-and-down dont-break-out word-wrap">
                {{ item.phone }}
              </td>
            </template>
            <template v-slot:item.email="{ item }">
              <td class="hidden-sm-and-down dont-break-out word-wrap">
                {{ item.email }}
              </td>
            </template>
            <template v-slot:item.contactName="{ item }">
              <td class="hidden-sm-and-down dont-break-out word-wrap">
                {{ item.contactName }}
              </td>
            </template>
            <template v-slot:item.contactPhone="{ item }">
              <td class="hidden-sm-and-down dont-break-out word-wrap">
                {{ item.contactPhone }}
              </td>
            </template>
            <template v-slot:item.contactEmail="{ item }">
              <td class="hidden-sm-and-down dont-break-out word-wrap">
                {{ item.contactEmail }}
              </td>
            </template>
            <template v-slot:item.actions="{ item }">
              <td class="justify-center layout px-0">
                <v-menu bottom auto>
                  <template v-slot:activator="{ on }">
                    <v-btn icon v-on="on">
                      <v-icon>more_vert</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="editCompany(item)">
                      <v-list-item-action>
                        <v-icon color="primary">mode_edit</v-icon>
                      </v-list-item-action>
                      <v-list-item-title>{{
                        resx.labels.edit
                      }}</v-list-item-title>
                    </v-list-item>
                    <v-divider></v-divider>
                    <v-list-item
                      @click="deleteCompany(item)"
                    >
                      <v-list-item-action>
                        <v-icon color="error">delete</v-icon>
                      </v-list-item-action>
                      <v-list-item-title>{{
                        resx.labels.delete
                      }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <v-row>
        <div>
          <v-btn
            color="accent"
            class="addButton"
            fab
            bottom
            right
            absolute
            @click="newCompany()"
          >
            <v-icon>add</v-icon>
          </v-btn>
        </div>
      </v-row>
      <v-row>
        <v-dialog
          persistent
          :value="showEditCompany" 
          @input="cancelEdit()" width="800"
          @keydown.esc="cancel()"
        >
          <!-- v-if, no solo el :value del dialogo: v-dialog usa el mixin
               bootable, que una vez abierto deja el contenido montado para
               siempre (hasContent = isBooted || eager || isActive). Sin esto el
               formulario sobrevive al cierre con company en null -de ahi los
               errores de consola- y en la segunda apertura no corre su
               mounted(), asi que nunca vuelve a sacar la foto de la empresa y
               al cerrar avisa "hay cambios sin guardar" aunque no se haya
               tocado nada. -->
          <edit-company
            v-if="companyForEdit"
            :company="companyForEdit"
            @cancel="cancelEdit()"
            @saved="companySaved($event)"
          ></edit-company>
        </v-dialog>
      </v-row>
    </v-card-text>
  </v-card>
</template>
<script>
import companyService from "@/services/CompanyService";
import editCompany from "@/components/views/companies/edit-company.view.vue";
import BarLoader from "@/components/common/bar-loader.vue";

export default {
  async mounted() {
    this.companies = await companyService.getCompanies(true);
    this.loading = false;
  },
  components: { editCompany, BarLoader },
  data() {
    const resx = this.$store.state.locale.resx;
    return {
      itemsPerPage: parseInt(localStorage.getItem(this.$options.name + '_itemsPerPage')) || 50,
      search: "",
      showEditCompany: false,
      companyForEdit: null,
      headers: [
        {
          text: resx.labels.companyName,
          value: "name"
        },
        {
          class: "hidden-sm-and-down",
          text: resx.labels.uniqueIdentifier,
          value: "identifier.number"
        },
        {
          class: "hidden-sm-and-down",
          text: resx.labels.state,
          value: "isActive"
        },
        {
          class: "hidden-sm-and-down",
          text: resx.labels.phone,
          value: "phone"
        },
        {
          class: "hidden-sm-and-down",
          text: resx.labels.email,
          value: "email"
        },
        {
          class: "hidden-sm-and-down",
          text: resx.labels.contactName,
          value: "contactName"
        },
        {
          class: "hidden-sm-and-down",
          text: resx.labels.contactPhone,
          value: "contactPhone"
        },
        {
          class: "hidden-sm-and-down",
          text: resx.labels.contactEmail,
          value: "contactEmail"
        },
        {
          text: resx.labels.actions,
          align: "center",
          sortable: false,
          value: "actions"
        }
      ],
      companies: [],
      loading: true
    };
  },
  methods: {
    saveItemsPerPage(val) {
      this.itemsPerPage = val;
      localStorage.setItem(this.$options.name + '_itemsPerPage', val);
    },
    editCompany(company) {
      const copy = JSON.parse(JSON.stringify(company));
      if (!copy.allowedRoles) copy.allowedRoles = [];
      if (!copy.allowedUsers) copy.allowedUsers = [];
      this.companyForEdit = copy;
      this.showEditCompany = true;
    },
    newCompany() {
      this.companyForEdit = {
        name: null,
        identifier: {
          type: null,
          number: null
        },
        isActive: true,
        allowedRoles: [],
        allowedUsers: []
      };
      this.showEditCompany = true;
    },
    deleteCompany(company) {
      const vm = this;
      var sprintf = require("sprintf-js").sprintf;

      this.botConfirmDialog({
        title: `${
          this.resx.labels.delete
        } ${this.resx.labels.company.toLowerCase()}`,
        content: sprintf(this.resx.messages.deleteCompanyMessage, company.name),
        alertHint: this.resx.messages.deleteCompanyAlertHint
      }).then(function() {
        companyService.deleteCompany(company._id).then(() => {
          const index = vm.companies.indexOf(company);
          vm.companies.splice(index, 1);
        });
      });
    },
    cancelEdit() {
      this.showEditCompany = false;
      this.companyForEdit = null;
    },
    companySaved(result) {
      if (result.isNew) {
        this.companies.push(result.company);
      } else {
        let index = this.companies.findIndex(company => {
          return company._id == result.company._id;
        });
        this.$set(this.companies, index, result.company);
      }
      this.cancelEdit();
    },
  },
  computed: {
    count: function() {
      let currentItems = this.$refs.dataTable.internalCurrentItems;
      let count = Object.keys(currentItems).length;
      return count;
    }
  }
};
</script>

<style lang="scss" scoped>
#companies {
  .v-card {
    height: 100%;
  }
}
</style>
